/**
 * 自动保存Hook
 *
 * 提供表单自动保存功能，避免数据丢失
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';

// 简单的防抖函数实现
function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T & { cancel: () => void } {
  let timeoutId: NodeJS.Timeout;

  const debouncedFn = ((...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T & { cancel: () => void };

  debouncedFn.cancel = () => {
    clearTimeout(timeoutId);
  };

  return debouncedFn;
}

interface AutoSaveOptions {
  // 自动保存间隔（毫秒）
  interval?: number;
  // 防抖延迟（毫秒）
  debounceDelay?: number;
  // 是否启用自动保存
  enabled?: boolean;
  // 保存函数
  onSave: (data: any) => Promise<void>;
  // 保存成功回调
  onSaveSuccess?: (data: any) => void;
  // 保存失败回调
  onSaveError?: (error: any) => void;
  // 是否显示保存状态消息
  showMessage?: boolean;
  // 最大重试次数
  maxRetries?: number;
}

interface AutoSaveState {
  // 是否正在保存
  saving: boolean;
  // 上次保存时间
  lastSaved: Date | null;
  // 保存状态
  saveStatus: 'idle' | 'saving' | 'success' | 'error';
  // 错误信息
  error: string | null;
  // 重试次数
  retryCount: number;
}

export const useAutoSave = (options: AutoSaveOptions) => {
  const {
    interval = 30000, // 30秒
    debounceDelay = 2000, // 2秒防抖
    enabled = true,
    onSave,
    onSaveSuccess,
    onSaveError,
    showMessage = true,
    maxRetries = 3,
  } = options;

  const [state, setState] = useState<AutoSaveState>({
    saving: false,
    lastSaved: null,
    saveStatus: 'idle',
    error: null,
    retryCount: 0,
  });

  const intervalRef = useRef<NodeJS.Timeout>();
  const pendingDataRef = useRef<any>(null);
  const isManualSaveRef = useRef(false);

  // 执行保存
  const performSave = useCallback(
    async (data: any, isManual = false) => {
      if (state.saving && !isManual) return;

      setState(prev => ({
        ...prev,
        saving: true,
        saveStatus: 'saving',
        error: null,
      }));

      try {
        await onSave(data);

        setState(prev => ({
          ...prev,
          saving: false,
          saveStatus: 'success',
          lastSaved: new Date(),
          error: null,
          retryCount: 0,
        }));

        if (showMessage && isManual) {
          message.success('保存成功');
        }

        onSaveSuccess?.(data);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : '保存失败';

        setState(prev => ({
          ...prev,
          saving: false,
          saveStatus: 'error',
          error: errorMessage,
          retryCount: prev.retryCount + 1,
        }));

        if (showMessage) {
          message.error(`保存失败: ${errorMessage}`);
        }

        onSaveError?.(error);

        // 如果是自动保存且未达到最大重试次数，则稍后重试
        if (!isManual && state.retryCount < maxRetries) {
          setTimeout(
            () => {
              if (pendingDataRef.current) {
                performSave(pendingDataRef.current, false);
              }
            },
            5000 * (state.retryCount + 1)
          ); // 递增重试间隔
        }
      }
    },
    [
      onSave,
      onSaveSuccess,
      onSaveError,
      showMessage,
      state.saving,
      state.retryCount,
      maxRetries,
    ]
  );

  // 防抖保存函数
  const debouncedSave = useCallback(
    debounce((data: any) => {
      if (enabled && !isManualSaveRef.current) {
        performSave(data, false);
      }
      isManualSaveRef.current = false;
    }, debounceDelay),
    [performSave, enabled, debounceDelay]
  );

  // 手动保存
  const manualSave = useCallback(
    (data: any) => {
      isManualSaveRef.current = true;
      debouncedSave.cancel(); // 取消防抖保存
      return performSave(data, true);
    },
    [performSave, debouncedSave]
  );

  // 触发自动保存
  const triggerAutoSave = useCallback(
    (data: any) => {
      if (!enabled) return;

      pendingDataRef.current = data;
      debouncedSave(data);
    },
    [enabled, debouncedSave]
  );

  // 设置定时保存
  useEffect(() => {
    if (!enabled) return;

    intervalRef.current = setInterval(() => {
      if (pendingDataRef.current && !state.saving) {
        performSave(pendingDataRef.current, false);
      }
    }, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, interval, performSave, state.saving]);

  // 清理资源
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [debouncedSave]);

  // 重置状态
  const reset = useCallback(() => {
    setState({
      saving: false,
      lastSaved: null,
      saveStatus: 'idle',
      error: null,
      retryCount: 0,
    });
    pendingDataRef.current = null;
    debouncedSave.cancel();
  }, [debouncedSave]);

  // 格式化上次保存时间
  const getLastSavedText = useCallback(() => {
    if (!state.lastSaved) return '未保存';

    const now = new Date();
    const diff = now.getTime() - state.lastSaved.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return '刚刚保存';
    if (minutes < 60) return `${minutes}分钟前保存`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}小时前保存`;

    const days = Math.floor(hours / 24);
    return `${days}天前保存`;
  }, [state.lastSaved]);

  return {
    // 状态
    ...state,

    // 方法
    triggerAutoSave,
    manualSave,
    reset,

    // 辅助信息
    lastSavedText: getLastSavedText(),
    canSave: !state.saving,
    hasUnsavedChanges:
      pendingDataRef.current !== null && state.saveStatus !== 'success',
  };
};

export default useAutoSave;
